/**
 * rng/ — seeded-PRNG (D7, tech/21 §8).
 *
 * Алгоритм ФИКСИРОВАН: mulberry32 (32-битное состояние). `RngState` — чистый
 * сериализуемый JSON и живёт в `GameState.rng`; любая случайность продвигает
 * его детерминированной операцией. Один и тот же `RngState` + одна и та же
 * последовательность вызовов ⇒ одни и те же числа (реплеи, серверная валидация).
 *
 * Внутренний примитив `next` НЕ экспортируется из пакета (F4): картам доступна
 * только поверхность int/chance/pick/shuffle (через ctx.rng, tech/22 §4).
 * Все публичные функции ЧИСТЫЕ: вход не мутируется, новый RngState возвращается.
 */
/**
 * Создать состояние PRNG из числового seed. Seed обязан быть целым
 * (NaN/Infinity/дробные — ошибка, а не тихая коэрция); целые вне uint32
 * детерминированно нормализуются `>>> 0` (отрицательные/большие — валидны).
 */
export function createRng(seed) {
    if (!Number.isInteger(seed)) {
        throw new Error(`createRng: seed должен быть целым числом (получено ${seed})`);
    }
    return { algo: 'mulberry32', state: seed >>> 0 };
}
/**
 * ВНУТРЕННИЙ примитив: один шаг mulberry32.
 * Возвращает равномерное число в [0, 1) и продвинутое состояние.
 * НЕ экспортируется из пакета (F4) — карты видят только int/chance/pick/shuffle.
 */
function next(rng) {
    const s = (rng.state + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    const u = ((t ^ (t >>> 14)) >>> 0) / 0x1_0000_0000;
    return [u, { algo: 'mulberry32', state: s }];
}
/** Целое в диапазоне [min, max] ВКЛЮЧИТЕЛЬНО. Чистая функция: вход не мутируется. */
export function rngInt(rng, min, max) {
    if (!Number.isInteger(min) || !Number.isInteger(max)) {
        throw new Error(`rngInt: границы должны быть целыми (получено min=${min}, max=${max})`);
    }
    if (min > max) {
        throw new Error(`rngInt: min (${min}) больше max (${max})`);
    }
    // Один шаг mulberry32 даёт 32 бита энтропии: диапазон шире 2^32 дал бы
    // разреженные значения (только кратные шагу) — запрещаем явно.
    if (max - min + 1 > 2 ** 32) {
        throw new Error(`rngInt: диапазон [${min}, ${max}] шире 2^32 — не покрывается одним шагом PRNG`);
    }
    const [u, advanced] = next(rng);
    return [min + Math.floor(u * (max - min + 1)), advanced];
}
/**
 * Бросок «с шансом p» (p в [0, 1]). Всегда потребляет ровно один шаг PRNG —
 * даже при p=0/p=1 — чтобы число потреблённых шагов не зависело от аргумента.
 */
export function rngChance(rng, p) {
    if (!Number.isFinite(p) || p < 0 || p > 1) {
        throw new Error(`rngChance: p должно быть числом в [0, 1] (получено ${p})`);
    }
    const [u, advanced] = next(rng);
    return [u < p, advanced];
}
/** Равновероятный выбор одного элемента непустого массива. Вход не мутируется. */
export function rngPick(rng, arr) {
    if (arr.length === 0) {
        throw new Error('rngPick: массив пуст');
    }
    const [i, advanced] = rngInt(rng, 0, arr.length - 1);
    return [arr[i], advanced];
}
/**
 * Тасовка Фишера–Йейтса. Возвращает НОВЫЙ массив-перестановку (без потерь
 * и дублей); входной массив не мутируется.
 */
export function rngShuffle(rng, arr) {
    const out = arr.slice();
    let cur = rng;
    for (let i = out.length - 1; i > 0; i--) {
        const [j, advanced] = rngInt(cur, 0, i);
        cur = advanced;
        const tmp = out[i];
        out[i] = out[j];
        out[j] = tmp;
    }
    return [out, cur];
}
//# sourceMappingURL=rng.js.map